<script lang="ts">
  /**
   * Settings: the shell's `SettingsPage` template, with Interlock's groups
   * (`docs/decisions/0054`). Reached from the profile sheet on a menu and from
   * the Game menu in a game. The parent owns `open`; every way of closing
   * calls `onclose`.
   *
   * - **Sound**: Music and Sound effects, each a `ToggleRow`, each with its
   *   volume (a bare `SliderRow`) on a second line of the same row while it
   *   is on. Every change goes straight to the live `AudioEngine`, so it is
   *   heard at once if a run is in progress; before the first gesture the
   *   engine has no `AudioContext` yet and its setters only update the
   *   numbers `start()` will read (`audio/engine.ts`).
   * - **Look**: the app's own colours, as the shell prescribes them
   *   (`packages/shell/INTEGRATION.md` 4.5, `docs/decisions/0056`): the
   *   appearance, Light (the default) or Dark, and the accent colour under it
   *   (the shell's `AccentPicker`: Interlock's signature blue first, named
   *   "Interlock", then the shared presets, or Custom with two sliders). A
   *   slider's every move is shown at once; the colour is saved when it is
   *   let go, or when a preset is pressed. These are the only colours a
   *   player chooses: the board has one look of its own, which follows the
   *   appearance (a dark board on Dark, a light one on Light).
   * - **More**: Controls, How to play, Install (while the device offers it)
   *   and About. Each is a page of its own laid over this one, with its own
   *   history entry, so Back steps out one level at a time and Settings is
   *   still open underneath (`docs/decisions/0047`).
   *
   * The build this is sits at the page's foot (the template's required
   * `buildId`): the deploy gate greps the served bundle for its commit, and a
   * player reading it to a developer reads the same value.
   */
  import {
    AccentPicker,
    Group,
    InstallSheet,
    Row,
    RowBadge,
    SegmentedControl,
    SettingsPage,
    SliderRow,
    ToggleRow,
  } from '@artibens/app-shell';
  import type { SegmentOption } from '@artibens/app-shell';

  import { audio } from '../audio/engine.js';
  import {
    IOS_INSTALL_INTRO,
    IOS_INSTALL_SHEET_TITLE,
    IOS_INSTALL_STEPS,
  } from '../copy/install.js';
  import GameGlyph from '../glyphs/GameGlyph.svelte';
  import { install, promptInstall } from '../state/install.svelte.js';
  import { persistSettings, settings } from '../state/settings.svelte.js';
  import type { Appearance } from '../state/settings-io.js';
  import { INTERLOCK_SIGNATURE } from '../theme/accent.js';
  import { showAccent } from '../theme/show-accent.js';
  import AboutPage from './AboutPage.svelte';
  import ControlsPage from './ControlsPage.svelte';
  import HowToPlayPage from './HowToPlayPage.svelte';

  const { open, onclose }: { open: boolean; onclose: () => void } = $props();

  /** The seven characters of the commit the bundle was built from. */
  const BUILD_ID = __COMMIT_SHA__.slice(0, 7);

  let subView = $state<'controls' | 'how-to-play' | 'about' | 'install' | null>(null);

  // Whatever closes Settings closes what is stacked on it too, so no
  // sub-view can outlive its parent and be found open on the next visit.
  $effect(() => {
    if (!open) subView = null;
  });

  function closeSubView(): void {
    subView = null;
  }

  /* --- sound ------------------------------------------------------------- */

  function setMusic(on: boolean): void {
    settings.musicOn = on;
    audio.setMusicEnabled(on);
    persistSettings();
  }

  function setMusicVolume(value: number): void {
    settings.musicVolume = value;
    audio.setMusicVolume(value);
    persistSettings();
  }

  function setSfx(on: boolean): void {
    settings.sfxOn = on;
    audio.setSfxEnabled(on);
    persistSettings();
  }

  function setSfxVolume(value: number): void {
    settings.sfxVolume = value;
    audio.setSfxVolume(value);
    persistSettings();
  }

  function percent(value: number): string {
    return `${String(Math.round(value * 100))} %`;
  }

  /* --- look -------------------------------------------------------------- */

  const appearanceOptions: SegmentOption<Appearance>[] = [
    { value: 'dark', label: 'Dark' },
    { value: 'light', label: 'Light' },
  ];

  function setAppearance(value: Appearance): void {
    settings.appearance = value;
    persistSettings();
  }

  /**
   * A colour chosen (a preset pressed, a slider let go): save it, and show
   * it. `App.svelte` shows the saved colour too, but a slider let go on the
   * colour its last move already showed changes nothing it reads.
   */
  function saveAccent(hex: string): void {
    settings.accent = hex;
    persistSettings();
    showAccent(hex, settings.appearance);
  }

  /** A slider moving: show the colour on the whole app, without saving it. */
  function previewAccent(hex: string): void {
    showAccent(hex, settings.appearance);
  }

  /* --- more -------------------------------------------------------------- */

  function activateInstall(): void {
    if (install.availability === 'installable') void promptInstall();
    else if (install.availability === 'ios-manual') subView = 'install';
  }
</script>

{#snippet musicBadge()}<RowBadge tone="purple" glyph="speaker" />{/snippet}
{#snippet sfxBadge()}<RowBadge tone="red" glyph="speaker" />{/snippet}
{#snippet accentBadge()}<RowBadge tone="purple" glyph="palette" />{/snippet}
{#snippet appearanceBadge()}<RowBadge tone="blue" glyph="moon" />{/snippet}
{#snippet controlsBadge()}<RowBadge tone="indigo"><GameGlyph name="pad" size={18} /></RowBadge
  >{/snippet}
{#snippet helpBadge()}<RowBadge tone="green" glyph="question" />{/snippet}
{#snippet installBadge()}<RowBadge tone="orange" glyph="download" />{/snippet}
{#snippet aboutBadge()}<RowBadge tone="blue" glyph="info" />{/snippet}

{#snippet accentPicker()}
  <AccentPicker
    label="Accent colour"
    value={settings.accent}
    base={settings.appearance}
    signature={INTERLOCK_SIGNATURE}
    onchange={saveAccent}
    oninput={previewAccent}
    testid="settings-accent"
    testids={{
      swatch: 'settings-accent-swatch',
      custom: 'settings-accent-custom',
      hue: 'settings-accent-hue',
      vividness: 'settings-accent-vividness',
      preview: 'settings-accent-preview',
      note: 'settings-accent-note',
      name: 'settings-accent-name',
    }}
  />
{/snippet}

{#snippet appearanceControl()}
  <SegmentedControl
    label="Appearance"
    value={settings.appearance}
    options={appearanceOptions}
    onchange={setAppearance}
    testid="settings-appearance"
  />
{/snippet}

{#snippet musicVolume()}
  <SliderRow
    bare
    label="Music volume"
    value={settings.musicVolume}
    valueText={percent(settings.musicVolume)}
    oninput={setMusicVolume}
    testid="settings-music-volume"
  />
{/snippet}

{#snippet sfxVolume()}
  <SliderRow
    bare
    label="Sound effects volume"
    value={settings.sfxVolume}
    valueText={percent(settings.sfxVolume)}
    oninput={setSfxVolume}
    testid="settings-sfx-volume"
  />
{/snippet}

{#snippet groups()}
  <Group name="Sound" testid="settings-sound">
    <ToggleRow
      label="Music"
      checked={settings.musicOn}
      badge={musicBadge}
      onchange={setMusic}
      children={settings.musicOn ? musicVolume : undefined}
      testid="settings-music"
    />
    <ToggleRow
      label="Sound effects"
      checked={settings.sfxOn}
      badge={sfxBadge}
      onchange={setSfx}
      children={settings.sfxOn ? sfxVolume : undefined}
      testid="settings-sfx"
    />
  </Group>

  <Group name="Look" testid="settings-look">
    <Row
      label="Appearance"
      badge={appearanceBadge}
      trailing={appearanceControl}
      testid="settings-appearance-row"
    />
    <Row
      label="Accent colour"
      badge={accentBadge}
      under={accentPicker}
      testid="settings-accent-row"
    />
  </Group>

  <Group name="More" testid="settings-more">
    <Row
      label="Controls"
      below={`DAS ${String(settings.dasMs)}ms · ARR ${String(settings.arrMs)}ms · keys`}
      badge={controlsBadge}
      chevron
      opensDialog
      testid="settings-controls"
      onclick={() => {
        subView = 'controls';
      }}
    />
    <Row
      label="How to play"
      badge={helpBadge}
      chevron
      opensDialog
      testid="settings-how-to-play"
      onclick={() => {
        subView = 'how-to-play';
      }}
    />
    {#if install.availability !== 'unavailable'}
      <Row
        label="Install Interlock"
        below={install.availability === 'ios-manual'
          ? 'Add it to your home screen'
          : 'Play from your home screen, even offline'}
        badge={installBadge}
        chevron
        testid="settings-install"
        onclick={activateInstall}
      />
    {/if}
    <Row
      label="About"
      badge={aboutBadge}
      chevron
      opensDialog
      testid="settings-about"
      onclick={() => {
        subView = 'about';
      }}
    />
  </Group>
{/snippet}

<SettingsPage
  {open}
  title="Settings"
  {onclose}
  {groups}
  buildId={BUILD_ID}
  testid="settings-sheet"
  testids={{ build: 'settings-build' }}
/>

<ControlsPage open={subView === 'controls'} onclose={closeSubView} />
<HowToPlayPage open={subView === 'how-to-play'} onclose={closeSubView} />
<AboutPage open={subView === 'about'} onclose={closeSubView} />
<InstallSheet
  open={subView === 'install'}
  title={IOS_INSTALL_SHEET_TITLE}
  intro={IOS_INSTALL_INTRO}
  steps={IOS_INSTALL_STEPS}
  testid="settings-install-steps"
  onclose={closeSubView}
/>
